import { Component, Vue } from 'vue-property-decorator'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import AppDatatable from '@core/components/AppDatatable.vue'
import { header } from './model/MemberMeta'
import { MemberMetaList } from './model/MemberMetaList'
import { UserMetaList } from '@core/model/common/UserMetaList'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'

@Component({
  components: {
    AppDatatable
  }
})
export class GroupProfileMixin extends Vue {
  @inject()
  protected $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  protected groupOid = ''
  protected groupName = ''
  protected userToAdd = ''
  protected memberList: MemberMetaList = null
  protected txtSearch = ''

  protected userMetaList: UserMetaList = null
  protected userOptions = []
  protected userMapping = {}
  protected roles = ['member', 'supervisor', 'proxy-supervisor', 'admin']
  protected selectedUsers = []
  protected updatedUsers = []

  protected updatedMembers = {}
  protected deletedMembers = []

  get list () {
    return !this.memberList ? [] : this.memberList.list
  }

  get columns () {
    return [
      { label: 'Username', field: 'name' },
      { label: 'Fullname', field: 'accountFullname' },
      { label: 'Company Code', field: 'accountSfaCode' },
      { label: 'MAS Rep. No', field: 'accountMasRepno' },
      { label: 'Mobile No.', field: 'accountMobile' },
      {
        label: 'Group Role',
        field: 'role',
        options: [
          { label: 'Member', value: 'member' },
          { label: 'Supervisor', value: 'supervisor' },
          { label: 'Proxy Supervisor', value: 'proxy-supervisor' },
          { label: 'Admin', value: 'admin' },
          { label: 'Delete', value: 'delete' }
        ]
      }
    ]
  }

  async onDelete () {
    if (this.list.length > 0) {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'Groups cannot be deleted while there are still user(s) in the group.'
      })
      return
    }
    const isConfirm = await this.uiService.confirm.show({
      type: 'warning',
      message: 'Confirm delete this group ?'
    })
    if (isConfirm) {
      await this.$api.account.remove({
        accountID: this.groupOid,
        accountName: this.groupName
      })
      await this.onBack()
    }
  }

  onBack () {
    this.$router.push({ path: '/group-list' })
  }

  onSelectUser ($event) {
    const value = $event.target.value
    this.selectedUsers.push(value)
    this.changeOptionStatus(value, true)
  }

  onRemoveUser (user) {
    const index = this.selectedUsers.indexOf(user)
    const value = this.selectedUsers[index]
    if (index !== -1) {
      this.changeOptionStatus(value, false)
      this.selectedUsers.splice(index, 1)
    }
  }

  // Enable or disable option after selected or removed
  changeOptionStatus (id, status: true | false) {
    const changedOption = this.userOptions.find((option) => option.value === id)
    changedOption.disabled = status
  }

  onChangeRole (user) {
    if (user.role === 'delete') {
      this.deletedMembers.push(user.oid)
      return
    }
    this.updatedUsers.push({
      id: user.oid,
      role: user.role
    })
  }

  async onSave () {
    this.updatedMembers = {}
    this.selectedUsers.forEach(user => {
      this.updatedMembers[user] = 'member'
    })
    this.updatedUsers.forEach(user => {
      this.updatedMembers[user.id] = user.role
    })
    await this.$api.account.saveMemberList(this.groupOid, {
      setMembers: JSON.stringify(this.updatedMembers),
      delMembers: JSON.stringify(this.deletedMembers)
    })
    await this.fetchData()
  }

  async fetchData () {
    this.selectedUsers = []
    this.updatedUsers = []
    this.userToAdd = ''

    // Remove brackets from JSON.stringify()
    const headerParam = header.map((val) => JSON.stringify(val)).join(',')

    // Fetch member list data
    const res = await this.$api.account.memberList(this.groupOid, {
      headers: `[${headerParam}]`
    })
    this.memberList = new MemberMetaList(res.data)
    this.groupName = res.groupID_names[0]

    // Fetch user list data
    const userHeader = ['_oid', 'names', 'fullname']
    const resUser = await this.$api.account.list({
      groupStatus: 'user',
      headers: JSON.stringify(userHeader)
    })
    this.userMetaList = new UserMetaList(resUser.data)
    this.userOptions = this.userMetaList.list.map(user => {
      // User mapping with oid as key for simplicity
      this.userMapping[user.oid] = user.names[0]

      return {
        label: user.names[0],
        value: user.oid,
        disabled: false
      }
    })
  }

  async mounted () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    this.groupOid = this.$router.currentRoute.params.groupOid
    await this.fetchData()
  }
}
